// NASA FIRMS thermal-anomaly detections on the map (issue #93).
//
// Every visual decision here follows from what the data is, so they are worth
// stating once:
//
//   * A CIRCLE, NOT A PIN. A FIRMS record is the centre of a satellite pixel
//     flagged as containing one or more fires — NASA's own wording — and the
//     fire is usually smaller than the pixel. A pin would claim a point location
//     the product does not have, so the cell is drawn at its true size on the
//     ground and the operator sees the real precision.
//
//   * A SECOND, FIXED-SIZE RING. A 375 m VIIRS cell is about two pixels wide at
//     the map's default zoom 13 — effectively invisible and impossible to tap.
//     The dashed ring is a locator at constant screen size; the filled circle
//     inside it is the actual cell. At high zoom the cell grows past the ring,
//     which is the point.
//
//   * NOT LIKE A SENSOR NODE. Nodes are teardrop pin images with a small status
//     dot. These are hollow dashed rings in warm colours, with no pin glyph, so
//     the two cannot be confused at a glance.
//
//   * NEVER "A FIRE". Every label says "thermal anomaly", carries the
//     confidence FIRMS published, and carries the acquisition time. A detection
//     with no confidence value says so rather than being shown bare.
import { Fragment } from "react";
import { Circle, CircleMarker, Tooltip, Popup } from "react-leaflet";
import L from "leaflet";

// Warm colours, graded by the confidence FIRMS assigned. Low confidence is
// muted and dashed because NASA associates it with sun glint and cooler
// anomalies — it is the class most likely not to be a fire at all.
const CONFIDENCE_STYLE = {
  high: { color: "#b91c1c", dash: null, fill: 0.4 },
  nominal: { color: "#ea580c", dash: null, fill: 0.3 },
  low: { color: "#eab308", dash: "4 4", fill: 0.18 },
};

// A detection with no confidence class at all — MODIS reports a percentage
// instead, and a legacy or malformed row may carry neither. Grey rather than a
// warm colour: an unclassified detection must not read as a graded hazard.
const UNCLASSIFIED_STYLE = { color: "#78716c", dash: "2 4", fill: 0.18 };

// Fixed screen radius of the locator ring, in pixels.
const LOCATOR_RADIUS_PX = 9;

// A detection inside the alert radius of a node gets a heavier stroke. Colour
// still carries confidence — proximity does not make a low-confidence anomaly
// more likely to be real, it only makes it more worth looking at.
const NEAR_NODE_WEIGHT = 3;
const DEFAULT_WEIGHT = 1.5;

function styleFor(detection) {
  const base =
    CONFIDENCE_STYLE[detection.confidence] ??
    // MODIS gives a percentage and no class. It is a real, published confidence
    // value, so it is graded rather than lumped in with "unclassified" — but the
    // breakpoints are ours, not NASA's, so they only pick a colour and the exact
    // percentage is always what the label shows.
    (typeof detection.confidence_pct === "number"
      ? detection.confidence_pct >= 80
        ? CONFIDENCE_STYLE.high
        : detection.confidence_pct >= 30
          ? CONFIDENCE_STYLE.nominal
          : CONFIDENCE_STYLE.low
      : UNCLASSIFIED_STYLE);
  return {
    ...base,
    weight: detection.near_node === true ? NEAR_NODE_WEIGHT : DEFAULT_WEIGHT,
  };
}

// "2h ago" / "3d ago". Acquisition age matters more here than for a sensor
// reading: a detection from twelve hours ago says nothing about now, and the
// satellite has probably not passed since.
//
// Exported because App.js's fire banner needs the same figure. A detection's age
// is not optional decoration there: a banner that names a distance and a
// confidence but not a time invites it to be read as current.
export function formatDetectionAge(iso, t) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return t("fireAgeMinutes", { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("fireAgeHours", { n: hours });
  return t("fireAgeDays", { n: Math.floor(hours / 24) });
}

// HH:MM UTC. UTC is stated explicitly rather than converted to the browser's
// zone: FIRMS acquisition times are UTC, and a silently localised time cannot be
// cross-checked against NASA's own FIRMS map.
function formatUtc(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function confidenceLabel(detection, t) {
  if (detection.confidence)
    return t(`fireConfidence_${detection.confidence}`);
  if (typeof detection.confidence_pct === "number")
    return t("fireConfidencePct", { n: detection.confidence_pct });
  return t("fireConfidenceUnknown");
}

// Shared label body, rendered inside both the hover tooltip and the tap popup.
// Confidence and acquisition time are unconditional: there is no branch of this
// function that shows a detection without them.
function DetectionLabel({ detection, alertRadiusKm, nodesStatus, t }) {
  const age = formatDetectionAge(detection.acquired_at, t);
  const nearest = detection.nearest_node;

  return (
    <div style={{ minWidth: 210, fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>
        🔥 {t("fireThermalAnomaly")}
      </div>
      {/* The disclaimer sits directly under the title, not in a footnote. It is
          the single most important thing about this data. */}
      <div style={{ fontSize: 11, color: "#b45309", marginBottom: 5 }}>
        {t("fireNotConfirmed")}
      </div>

      <div style={{ fontSize: 12, color: "#374151" }}>
        {t("fireConfidenceLabel")}: <strong>{confidenceLabel(detection, t)}</strong>
      </div>
      <div style={{ fontSize: 12, color: "#374151" }}>
        {t("fireAcquired")}: {formatUtc(detection.acquired_at) ?? "—"}
        {age ? ` (${age})` : ""}
      </div>

      {/* Distance to the nearest node is the number that drives a decision, so
          it is above the satellite metadata rather than below it. */}
      {nearest ? (
        <div style={{
          fontSize: 12, marginTop: 5, fontWeight: 600,
          color: detection.near_node ? "#b91c1c" : "#374151",
        }}>
          {t("fireDistanceToNode", { km: nearest.distance_km.toFixed(1), node: nearest.label })}
          {detection.near_node && ` · ${t("fireWithinRadius", { km: alertRadiusKm })}`}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "#b45309", marginTop: 5 }}>
          {nodesStatus === "unavailable"
            ? t("fireDistanceUnknown")
            : t("fireNoNodes")}
        </div>
      )}

      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 5 }}>
        {detection.instrument ?? "?"}
        {detection.satellite ? ` · ${detection.satellite}` : ""}
        {detection.source ? ` · ${detection.source}` : ""}
      </div>
      {typeof detection.frp_mw === "number" && (
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          {t("fireRadiativePower")}: {detection.frp_mw.toFixed(1)} MW
        </div>
      )}
      {/* Three decimals, ~100 m, and labelled as the cell's centre.
          FIRMS reports the centre of a detection pixel, not the position of the
          heat source: the anomaly is somewhere inside a ~375 m cell, and VIIRS
          geolocation adds error of its own on top. Four decimals is ~11 m, so the
          old form quoted a position an order of magnitude more precise than the
          cell size printed beside it — the same overstatement this component
          exists to avoid everywhere else. */}
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
        {t("fireCellCentre")}: {detection.latitude?.toFixed(3)}, {detection.longitude?.toFixed(3)}
        {" · "}
        {t("fireCellSize", { m: Math.round((detection.cell_radius_m ?? 0) * 2) })}
      </div>
    </div>
  );
}

// `simulated` has no branch here on purpose: the layer is only mounted for
// superusers, the tier that may call /api/. Lower tiers never get a fire layer
// at all rather than a generated one — inventing fire detections and drawing
// them on a real map is a different order of misleading from generating a tilt
// reading, and there is no fake-data precedent worth setting for it.
export default function FireDetectionsLayer({
  detections = [],
  alertRadiusKm = 10,
  nodesStatus = "ok",
  isTouch = false,
  t = (k) => k,
}) {
  return (
    <>
      {detections.map((detection) => {
        if (
          typeof detection.latitude !== "number" ||
          typeof detection.longitude !== "number"
        )
          return null;

        const center = [detection.latitude, detection.longitude];
        const { color, dash, fill, weight } = styleFor(detection);
        const cellOptions = {
          color,
          weight,
          dashArray: dash,
          fillColor: color,
          fillOpacity: fill,
        };
        const ringOptions = {
          color,
          weight,
          dashArray: dash ?? "5 4",
          fillOpacity: 0,
        };

        const label = (
          <DetectionLabel
            detection={detection}
            alertRadiusKm={alertRadiusKm}
            nodesStatus={nodesStatus}
            t={t}
          />
        );

        // Without stopPropagation a click here reaches the map's own click
        // handler, which reads a map click as "add a node at this point" and
        // opens the add-pin dialog — the same trap the power-line layer already
        // has to step around in App.js.
        const handlers = { click: (e) => L.DomEvent.stopPropagation(e) };

        return (
          // A Fragment, not a wrapper element: MapContainer renders its children
          // into the Leaflet container div, so a real <div> here would put stray
          // markup inside the map pane. React-leaflet layers render no DOM of
          // their own and attach through context.
          <Fragment key={detection.detection_id}>
            {/* The cell: true size on the ground. */}
            <Circle
              center={center}
              radius={detection.cell_radius_m ?? 500}
              pathOptions={cellOptions}
              eventHandlers={handlers}
            >
              {/* A tooltip is a hover idiom, so on touch it would take a tap to
                  open and a second tap to dismiss, with the first tap landing on
                  the map underneath (#61). Touch gets the popup only. */}
              {!isTouch && (
                <Tooltip direction="top" opacity={1} interactive={true}>
                  {label}
                </Tooltip>
              )}
              <Popup>{label}</Popup>
            </Circle>

            {/* The locator: constant screen size so the detection is findable
                and tappable at any zoom. Hollow, so it cannot be mistaken for
                the cell it surrounds. */}
            <CircleMarker
              center={center}
              radius={LOCATOR_RADIUS_PX}
              pathOptions={ringOptions}
              eventHandlers={handlers}
            >
              {!isTouch && (
                <Tooltip direction="top" opacity={1} interactive={true}>
                  {label}
                </Tooltip>
              )}
              <Popup>{label}</Popup>
            </CircleMarker>
          </Fragment>
        );
      })}
    </>
  );
}
